import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import SkeletonGrid from "@/components/SkeletonGrid";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Heart, Flag, VolumeX, Volume2, MessageSquare, MessagesSquare } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/dashboard/PageHeader";

export default function Community() {
    const { user } = useAuth();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [mutes, setMutes] = useState([]);
    const [reportOpen, setReportOpen] = useState(false);
    const [reportTarget, setReportTarget] = useState(null);
    const [reportReason, setReportReason] = useState("");
    // Feed scope — institution users can toggle between their institution
    // feed and the platform feed (item 11 of the Feb 2026 spec).
    const hasAgency = !!user?.agency_id;
    const [scope, setScope] = useState(hasAgency ? "institution" : "platform");

    async function load() {
        try {
            const [{ data: p }, { data: m }] = await Promise.all([
                api.get(`/community/posts?scope=${scope}`),
                api.get("/community/mutes"),
            ]);
            setPosts(p);
            setMutes(m.muted_user_ids || []);
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);

    async function submit() {
        if (!text.trim()) return;
        setBusy(true);
        try {
            await api.post("/community/posts", { content: text, scope });
            setText("");
            await load();
            toast.success(scope === "institution" ? "Post shared with your institution" : "Post shared with the community");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't publish your post — please try again.");
        } finally { setBusy(false); }
    }

    async function like(id) {
        await api.post(`/community/posts/${id}/like`);
        await load();
    }

    function openReport(post) {
        setReportTarget(post);
        setReportReason("");
        setReportOpen(true);
    }

    async function submitReport() {
        if (!reportReason.trim()) {
            toast.error("Please describe the reason");
            return;
        }
        try {
            await api.post(`/community/posts/${reportTarget.id}/report`, { reason: reportReason.trim() });
            toast.success("Report submitted — our moderators will review shortly");
            setReportOpen(false);
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't submit report — please try again.");
        }
    }

    async function toggleMute(post) {
        const isMuted = mutes.includes(post.user_id);
        try {
            await api.post(isMuted ? "/community/unmute" : "/community/mute", { target_user_id: post.user_id });
            toast.success(isMuted ? `Unmuted ${post.author}` : `Muted ${post.author} — their posts are hidden from you.`);
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't update mute — please try again.");
        }
    }

    return (
        <>
            <PageHeader
                icon={MessagesSquare}
                eyebrow="Trade"
                title="Who Is Around"
                subtitle="Talk shop with fellow climbers. Auto-flagged: aggressive advice, scam-like language, guarantees. Report abuse or mute a user to personalise your feed."
                actions={hasAgency && (
                    <div className="inline-flex rounded-lg border border-border overflow-hidden text-xs" data-testid="community-scope-tabs">
                        <button
                            onClick={() => setScope("institution")}
                            data-testid="community-scope-institution"
                            className={"px-3 min-h-[44px] " + (scope === "institution" ? "bg-primary text-primary-foreground font-semibold" : "hover:bg-secondary/40")}
                        >My institution</button>
                        <button
                            onClick={() => setScope("platform")}
                            data-testid="community-scope-platform"
                            className={"px-3 min-h-[44px] border-l border-border " + (scope === "platform" ? "bg-primary text-primary-foreground font-semibold" : "hover:bg-secondary/40")}
                        >Whole platform</button>
                    </div>
                )}
            />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
                {/* Composer (sticky on desktop) */}
                <div className="lg:col-span-4 order-1 lg:sticky lg:top-16 lg:self-start">
                    <div className="border border-border bg-card rounded-xl p-4" data-testid="post-composer">
                        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-3">
                            New post {scope === "institution" ? "· institution" : "· platform"}
                        </div>
                        <Textarea
                            placeholder="Share a setup, ask a question, drop knowledge…"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            className="mb-3 min-h-[100px]"
                            data-testid="post-textarea"
                        />
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-[10px] text-muted-foreground">
                                {text.length}/1000
                            </span>
                            <Button
                                onClick={submit}
                                disabled={busy || !text.trim()}
                                className="disabled:opacity-40 disabled:cursor-not-allowed min-h-[44px] px-4"
                                data-testid="post-submit"
                            >
                                Post
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Feed */}
                <div className="lg:col-span-8 order-2 space-y-3">
                    {loading && posts.length === 0 && <SkeletonGrid variant="rows" count={4} />}
                    {posts.map((p) => {
                        const isMuted = mutes.includes(p.user_id);
                        const isMine = p.user_id === user?.id;
                        return (
                            <div key={p.id} className="border border-border bg-card rounded-xl p-4 hover:border-primary/30 transition-colors" data-testid={`post-${p.id}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-bold shrink-0">
                                            {(p.author?.[0] || "?").toUpperCase()}
                                        </div>
                                        <div>
                                            <div className="font-semibold text-sm">{p.author}</div>
                                            <div className="text-[10px] text-muted-foreground mono">{new Date(p.ts).toLocaleString("en-KE")}</div>
                                        </div>
                                    </div>
                                    {p.flagged && (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-md border border-rose-500/30 bg-rose-500/10 text-rose-600 dark:text-rose-400">
                                            <Flag className="w-3 h-3" /> Auto-flagged
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm leading-relaxed mb-3">{p.content}</p>
                                <div className="flex items-center gap-4 text-xs pt-2 border-t border-border/60">
                                    <button className="inline-flex items-center gap-1 min-h-[32px] hover:text-primary transition-colors" onClick={() => like(p.id)} data-testid={`like-${p.id}`}>
                                        <Heart className="w-3.5 h-3.5" /> {p.likes}
                                    </button>
                                    {!isMine && (
                                        <>
                                            <button
                                                className="inline-flex items-center gap-1 min-h-[32px] text-muted-foreground hover:text-danger transition-colors"
                                                onClick={() => openReport(p)}
                                                data-testid={`report-${p.id}`}
                                            >
                                                <Flag className="w-3.5 h-3.5" /> Report
                                            </button>
                                            <button
                                                className="inline-flex items-center gap-1 min-h-[32px] text-muted-foreground hover:text-foreground transition-colors"
                                                onClick={() => toggleMute(p)}
                                                data-testid={`mute-${p.id}`}
                                            >
                                                {isMuted ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
                                                {isMuted ? "Unmute" : "Mute"}
                                            </button>
                                        </>
                                    )}
                                    {p.report_count > 0 && user?.role === "admin" && (
                                        <span className="inline-flex items-center gap-1 text-accent ml-auto">
                                            <Flag className="w-3.5 h-3.5" /> {p.report_count} report{p.report_count !== 1 ? "s" : ""}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    {!loading && posts.length === 0 && (
                        <EmptyState
                            icon={MessageSquare}
                            title="Nobody's posted yet."
                            body="Be the first to share a setup, ask a question, or drop knowledge for the community."
                            ctaLabel="Open the Terminal"
                            ctaTo="/app/terminal"
                            ctaTestId="community-empty-cta"
                            testId="community-empty"
                        />
                    )}
                </div>
            </div>

            <Dialog open={reportOpen} onOpenChange={setReportOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="tracking-tight">Report post</DialogTitle>
                    </DialogHeader>
                    <p className="text-xs text-muted-foreground mb-3">
                        Tell moderators why this post breaks community standards. Reports are anonymous to the post author.
                    </p>
                    <Input
                        placeholder="e.g. Guaranteed returns scam, personal attack, spam link"
                        value={reportReason}
                        onChange={(e) => setReportReason(e.target.value)}
                        data-testid="report-reason-input"
                    />
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="outline" onClick={() => setReportOpen(false)} className="min-h-[44px]">Cancel</Button>
                        <Button onClick={submitReport} data-testid="report-submit" className="min-h-[44px]">Submit report</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
